
import React from "react";
import { usePrayerGenerator } from "@/hooks/usePrayerGenerator";
import PrayerGeneratorForm from "@/components/PrayerGeneratorForm";
import PrayerResult from "@/components/PrayerResult";
import PrayerHeavenlyLoader from "@/components/PrayerHeavenlyLoader";
import { motion } from "framer-motion";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { BookOpen, MessageSquare, Heart } from "lucide-react";

const PrayerGenerator: React.FC = () => {
  useScrollToTop();
  
  const {
    prayerRequest,
    setPrayerRequest,
    selectedVoice,
    setSelectedVoice,
    loading,
    prayerResult,
    generatePrayer,
  } = usePrayerGenerator();

  if (loading) {
    return <PrayerHeavenlyLoader message="Preparing our hearts to pray and worship God..." />;
  }

  return (
    <div className="bg-white min-h-screen pt-16 md:pt-32">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto mb-8 md:mb-24 flex flex-col md:flex-row items-center gap-6 md:gap-8 pt-8 md:pt-0"
        >
          <div className="md:w-1/2 text-center md:text-left">
            <h1 className="text-3xl md:text-5xl font-playfair font-bold text-[#403E43] mb-4 md:mb-6">
              Scripture-Filled Prayer For Every Need
            </h1>
            <p className="text-base md:text-xl text-[#403E43]/80 mb-6 md:mb-8 font-butler">
              Let the Word guide your prayers through joy, suffering, surrender, and intimacy with Jesus.
            </p>
          </div>
          
          <div className="md:w-1/2">
            <img 
              src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//ai%20prayer.png" 
              alt="Prayer with Scripture" 
              className="rounded-lg shadow-xl max-w-full h-auto" 
            />
          </div>
        </motion.div>

        {/* Dynamic Features Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-6xl mx-auto mb-12 md:mb-24 bg-white rounded-2xl overflow-hidden shadow-lg border border-heaven-gold/10"
        >
          <div className="flex flex-col md:flex-row">
            <div className="order-2 md:order-1 md:w-1/2">
              <img 
                src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//ChatGPT%20Image%20Apr%202,%202025,%2012_25_39%20AM.png" 
                alt="Spiritual Prayer Journey" 
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="order-1 md:order-2 md:w-1/2 p-8">
              <h2 className="text-3xl font-playfair font-bold text-[#403E43] mb-6">
                Transform Your Prayer Life
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-heaven-gold/10 rounded-full flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-heaven-dark-gold" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-[#403E43]">Scripture-Infused Prayers</h3>
                    <p className="text-[#403E43]/80">Every prayer draws from the rich tapestry of God's Word, bringing biblical truth into your conversations with Him.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-heaven-gold/10 rounded-full flex items-center justify-center">
                    <Heart className="h-6 w-6 text-heaven-dark-gold" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-[#403E43]">Deeply Personal</h3>
                    <p className="text-[#403E43]/80">Prayers tailored to your unique situation, whether facing trials, celebrating joys, or seeking guidance in life's journey.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-heaven-gold/10 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-heaven-dark-gold" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-[#403E43]">Gospel-Centered Approach</h3>
                    <p className="text-[#403E43]/80">Rooted in historic Christian faith, these prayers help you connect with God's heart in every season of life.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
          
        {/* Prayer Companions Information */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-5xl mx-auto mb-8 md:mb-16 px-4 md:px-0"
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-heaven-dark-gold mb-3 md:mb-4">
            Your Personal Prayer Companions
          </h1>
          <p className="text-sm md:text-base text-[#403E43]/90">
            Feeling lost for words? Our AI prayer companions are here to help. Choose from our selection of compassionate voices 
            to find a prayer partner that speaks to your heart. 
            Whether you're experiencing joy, pain, or seeking guidance, these voices 
            can help you articulate your deepest prayers when words fail.
          </p>
        </motion.div>
          
        {/* Prayer Generator Form */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-5xl mx-auto"
        >
          <PrayerGeneratorForm
            prayerRequest={prayerRequest}
            onPrayerRequestChange={setPrayerRequest}
            selectedVoice={selectedVoice}
            onVoiceChange={setSelectedVoice}
            onSubmit={generatePrayer}
            loading={loading}
          />
          
          {/* Prayer Results */}
          {prayerResult && (
            <PrayerResult
              result={prayerResult}
              selectedVoice={selectedVoice}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PrayerGenerator;
